import {Exclude, Type} from "class-transformer";

import {ProjectType, serializeType} from "./common";
import {ContractModImpact, InternalContact} from "./contract";
import {ApprovalStatusType} from "../enums/approval-status-type.enum";
import {BillingType} from "../enums/billing-type.enum";
import {RevenueRecognitionType} from "@app/shared/enums/revenue-recognition-type.enum";
import {ExpenseMarkupType} from "@app/shared/enums/expense-markup-type.enum";


export enum ProjectStatusType {
  Planning = "PLANNING",
  Execution = "EXECUTION",
  Closeout = "CLOSEOUT"
}

export enum ProjectTaskConnectorType {
  Start_Finish = "start-finish",
  Finish_Start = "finish-start",
  Start_Start = "start-start",
  Finish_Finish = "finish-finish"
}

export class ProjectMilestone {
  id: number;
  version: number;
  name: string;
  description: string;
  sequenceNum: number;
  dueDateType?: string;
  progressType?: string;
  daysFromStart?: string;
  projectModId?: number;
  modNumber?: string;
  // statusId: number;

  plannedDueDate?: string;
  actualDeliveryDate: string;
  taskName?: string;
  projectTaskId: number;
  projectId?: number;
  estimateId?: number;
}

export class CurrentProjectLaborCategory {
  id: number;
  version: number;
  projectLaborCategoryId: number;
  effectiveThru: string;
  effectiveFrom: String;
  billingRate: number;
  yearIndex: number;
}

export class ProjectLaborCategory {
  id: number;
  version: number;

  billingRateDiscount?: number;
  billingRate: number;
  noRateEscalation?: boolean;
  hourCeiling?: number;
  displayTotalHoursBilled: number;

  periodLength: number;
  description?: string;
  laborCategoryCode: string;
  positionTitle: string;
  positionDuties?: string;
  qualification: string;
  customerCategory?: boolean;
  maxYearsExperience?: number;
  minYearsExperience?: number;
  projectModId?: number;
  modNumber?: string;
  // statusId: number;
  projectId?: number;
  locationType: string;
  estimateId?: number;
  contractLaborCategoryId?: number;
  degreeRequiredId?: number;
  positionDescriptionId?: number;
  certificationRequiredId?: number;
  contractBaseRate?: number;
  @Type(serializeType(CurrentProjectLaborCategory)) currentProjectLaborRate?: CurrentProjectLaborCategory;
}

export class ProjectWorkLocation {
  id: number;
  version: number;
  locationType: string;
  projectId?: number;
  estimateId?: number;
  officeLocationId?: number;
  clientLocationId?: number;
  officeLocationName?: string;
  clientLocationName?: string;
  addrName?: string;
  workLocationName?: string;    // used for sorting
  projectAddressName?: string;
}

export class ProjectLaborRate {
  id: number;
  version: number;
  yearIndex: string;
  effectiveFrom: string;
  effectiveThru: string;
  billingRate: number;
  laborCategoryId: number;
}


export class ProjectClinBudget {
  clinDescription: string;
  clinId: number;
  materialCeiling: number;
  feeCeiling: number;
  laborCeiling: number;
  odcCeiling: number;
  travelCeiling: number;
  trainingCeiling: number;
  optionId: number;
  optionPeriod: number;
  clinNumber: string;
}

export class ProjectInfo {
  id: number;
  name: string;
  description: string;
  currentStatus: ApprovalStatusType;
  hasSubmissionError?: boolean;
  startDate: string;
  endDate: string;

  contractId: number;
  contractTitle: string;
  projectBillingPlanId?: number;
  projectBillingPlanName?: string;
  obsId: number;
  obsName: string;
  catalogId: number;
  catalogName: string;
  customerId: number;
  customerBusinessName: string;
  billingType?: BillingType;
  programManager: string;
}

export class ContractProjectTaskYTD {
  id: number;
  version: number;
  year: string;
  employeeLaborYTD: number;
  subcontractorLaborYTD: number;
  materialYTD: number;
  odcYTD: number;
  travelYTD: number;
  trainingYTD: number;
  feeYTD: number;
  revenueYTD: number;
  projectId: number;
  contractId: number;
  projectTaskId: number;
  orgId: number;
}

export class Project {
  id: number; //
  version: number; //
  name: string; //
  projectNumber: string;
  lineOfBusinessId?: number;
  programManager: string;
  description: string; //
  isDirect: boolean = true;
  isOverTimeAllowed: boolean = false;
  isExistingProject: boolean = false;
  active: boolean = true;
  startDate?: string;
  endDate?: string;
  // budgetLaborCeiling?: number;
  budgetEmployeeLabor?: number;
  budgetSubcontractorLabor?: number;
  budgetMaterialCeiling?: number;
  budgetODCCeiling?: number;
  budgetFeeCeiling?: number;
  budgetTrainingCeiling?: number;
  budgetTravelCeiling?: number;
  employeeLaborITD?: number;
  subcontractorLaborITD?: number;
  materialITD?: number;
  odcITD?: number;
  travelITD?: number;
  trainingITD?: number;
  feeITD?: number;
  escalationRate?: number;
  billingType: BillingType;
  revenueAccountGroupId?: number;
  isRevenueAccountGroupMapped?: boolean;
  revenueRecognitionType?: RevenueRecognitionType;
  expenseMarkupType?: ExpenseMarkupType;
  revenueITD?: number;
  gandaPoolId?: number;
  materialHandlePoolId?: number;
  projectModId?: number;
  modNumber?: string;
  customerPONumber?: string;
  periodLength?: number; // labor period length
  projectStatusType: ProjectStatusType; //
  contractId: number; //
  contractTitle: string;
  customerId?: number;
  customerBusinessName?: string;
  projectBillingPlanId?: number;
  projectBillingPlanName?: string;
  chargeCodeId?: number;
  chargeNumber?: string;
  hasClins?: boolean;
  obsId: number; //
  obsName: string;
  projectCatalogId: number; //
  projectCatalogName: string;
  submittedById?: number;
  submittedForId?: number;
  totalValue?: number;
  currentStatusName?: ApprovalStatusType = ApprovalStatusType.NotAvailable;
  hasSubmissionError?: boolean;
  budgetNoneLaborCeiling?: number;
  actualNoneLaborCost?: number;
  pdfTemplateId?: number;
  contactId?: number;
  contactFullName?: string;
  contactEmail?: string;
  projectEstimateId?: number;
  billToAddressId?: number;
  billToAddressName?: string;
  shipToAddressId?: number;
  shipToAddressName?: string;
  hasApproved?: boolean;
  useProjectIndirectRates?: boolean;
  txnNumber?: string;

  @Type(serializeType(ProjectClinBudget)) budgets?: ProjectClinBudget[];
  @Type(serializeType(InternalContact)) contacts?: InternalContact[];
  @Type(serializeType(ProjectMilestone)) milestones?: ProjectMilestone[];
  @Type(serializeType(ProjectLaborCategory)) laborCategories?: ProjectLaborCategory[];
  @Type(serializeType(ProjectWorkLocation)) workLocations: ProjectWorkLocation[];
}

export class ProjectResourceRate {
  id: number;
  version: number;
  budgetHours: number = 0;
  effectiveFrom: string;
  effectiveThru: string;
  billable: boolean;
  resourceId: number;
  displayLaborRate: number;
  displayBudgetLaborCost: number;
  laborCostITD: number;
  displayActualHours: number;
  displayEffectiveFrom?: string;
}

export class ProjectResourceLocation {
  id: number;
  version: number;
  workLocationId: number;
  resourceId: number;
}

export class ProjectResource {
  id: number;
  version: number;
  effectiveFrom: string;
  effectiveThru: string;
  billable: boolean = true;
  noUserAssigned: boolean = false;
  noUserBudgetRate: number;
  userId: number;
  userFullName?: string;
  taskId: number;
  projectId?: number;
  projectModId?: number;
  modNumber?: string;
  estimateId?: number;
  laborCategoryId: number;
  positionTitle?: string;
  displayBudgetHours: number;
  displayBudgetEmployeeLaborCost?: number;
  displayBudgetSubcontractLaborCost?: number;
  employeeLaborITD?: number;
  subcontractorLaborITD?: number;
  displayActualHours?: number;
  workLocationFromHome?: boolean = false;
  @Type(serializeType(ProjectResourceLocation)) projectResourceLocations: ProjectResourceLocation[];
  @Type(serializeType(ProjectResourceRate)) resourceRates: ProjectResourceRate[];
}

export class TaskInfo {
  taskId?: number;
  startDate?: string;
  endDate?: string;
  taskName: string;
  contractClinNumber?: string;
  vendorBusinessName?: string;
  parentTaskName?: string;
  purchaseOrderNumber?: string;
}

export class AnyGanttConnector {
  connectTo: number;
  connectorType: ProjectTaskConnectorType;
}

export class AnyGanttNode {
  id: number;
  name: string;
  actualStart: string;
  actualEnd: string;
  progressValue: string;
  manager: string;
  chargeNumber: string;
  totalValue: number;
  children: AnyGanttNode[] = [];
  connector: AnyGanttConnector[] = [];
  addSubTaskIcon: string;
  showBudgetIcon: string;
  description: string;
}

export enum GanttChartColumns {
  WBSCol = 1,
  MoveTaskUpCol = 2,
  MoveTaskDownCol = 3,
  ChargeNumberCol = 4,
  TotalValueCol = 5,
  AddSubTaskCol = 6,
  EditTaskCol = 7,
  ShowBudgetCol = 8,
  ShowTaskModHistoryCol = 9,
  AddInitialTaskBalanceCol = 10,
  DeleteTaskCol = 11,
  GanttChartSplitterPosition = "56.5%",
  WBSColWidth = 280,
  ActionColWidth = 21
}

export class AnyGanttRoot {
  id: number;
  name: string;
  actualStart: string;
  actualEnd: string;
  manager: string;
  description: string;
  children: AnyGanttNode[] = [];
}

export class ProjectTaskConnector {
  id: number;
  version: number;
  connectFrom: number;
  connectTo: number;
  orgId: number;
  connectorType: ProjectTaskConnectorType;
}

export class ProjectTaskInfo {
  taskId: number;
  taskName: string;
  startDate: string;
  endDate: string;
  projectId: number;
  projectName?: string;
  contractClinNumber?: string;
  vendorBusinessName?: string;
  parentTaskName?: string;
  purchaseOrderNumber?: string;
}

export class ProjectTask {
  id: number;
  version: number;
  name: string;
  projectStatusType: ProjectStatusType;
  description: string;
  programManager: string;
  materialITD?: number;
  employeeLaborITD?: number;
  subcontractorLaborITD?: number;
  odcITD?: number;
  travelITD?: number;
  trainingITD?: number;
  budgetEmployeeLabor: number = 0;
  budgetSubcontractorLabor: number = 0;
  budgetMaterialCeiling: number = 0;
  budgetLaborCeiling?: number = 0;
  budgetFeeCeiling: number = 0;
  budgetODCCeiling: number = 0;
  budgetTrainingCeiling: number = 0;
  budgetTravelCeiling: number = 0;
  startDate: string;
  endDate: string;
  projectTaskConnectors: ProjectTaskConnector[] = [];
  rollupSubTasks?: boolean;
  projectId?: number;
  vendorId: number;
  purchaseOrderId?: number;
  parentTaskId: number;
  estimateId?: number;
  clinId?: number;
  projectModId?: number;
  modNumber?: string;
  sequenceNum?: number;

  @Type(serializeType(ProjectResource)) resources: ProjectResource[];
  @Type(serializeType(ProjectTask)) children: ProjectTask[] = [];
  taskInfo?: TaskInfo;
  chargeNumber?: string;
  totalValue: number;
  @Exclude({toClassOnly: true})
  parent?: ProjectTask; //Just for Treetable purpose should be removed when sending to backend
  expanded?: boolean; //Just for Treetable purpose should be removed when sending to backend
  availableBudgetEmployeeLabor?: number;
  availableBudgetSubcontractorLabor?: number;
  availableBudgetMaterialCeiling?: number;
  availableBudgetODCCeiling?: number;
  availableBudgetFeeCeiling?: number;
  availableBudgetTrainingCeiling?: number;
  availableBudgetTravelCeiling?: number;
  totalAvailableBudgets?: number;
}


export class ProjectInputInfo {
  projectId: number;
  projectName: string;
  projectStatusType: ProjectStatusType;
  projectDescription: string;
  projectManager: string;
  projectStartDate: string;
  projectEndDate: string;
  contractId: number;
  contractTitle: string;
  escalationRate: number;
  customerId: number;
  customerName: string;
  type: ProjectType;
  laborPeriodLength?: number;
  billingType?: BillingType;
  hasClins?: boolean;
  currentStatusName?: string;
  hasApproved?: boolean;
  isExistingProject?: boolean;
}


export class ProjectFinance {
  type: string;
  budget?: number;
  allocatedBudget?: number;
  remainingBudget?: number;
  costITD: number;
  costYTD: number;
}

export class ProjectRemainingBudget {
  id?: number;
  availableBudgetEmployeeLabor?: number;
  availableBudgetSubcontractorLabor?: number;

  availableBudgetMaterial?: number;
  availableBudgetOtherDirect?: number;
  availableBudgetFee?: number;
  availableBudgetTraining?: number;
  availableBudgetTravel?: number;
}

export class ProjectEstimateInfo {
  contactFullName: string = '';
  contractTitle: string = '';
  customerAddress: any;
  customerBusinessName: string = '';
  orgStructureName: string = '';
  projectEstimateId: number;
}


export class ProjectEstimate {
  id: number;
  version: number;
  revision: number;
  name: string;
  description: string;
  estimateValidUntil: string;
  proposedStartOn: string;
  proposedEndOn: string;
  escalationRate: number;
  ownerId: number; // why owner
  contractId: number;
  customerId: number;
  periodLength?: number; // labor period length
  obsId: number;
  submittedById: number;
  submittedForId: number;
  @Type(serializeType(InternalContact)) contacts?: InternalContact[];
  @Type(serializeType(ProjectMilestone)) milestones?: ProjectMilestone[];
  @Type(serializeType(ProjectLaborCategory)) laborCategories?: ProjectLaborCategory[];
  @Type(serializeType(ProjectWorkLocation)) workLocations?: ProjectWorkLocation[];
  projectEstimateInfo: ProjectEstimateInfo = new ProjectEstimateInfo();
  displayBudgetLaborCeiling?: number;
  displayBudgetODCCeiling?: number;
  displayBudgetMaterialCeiling?: number;
  displayBudgetFeeCeiling?: number;
  displayBudgetTravelCeiling?: number;
  displayBudgetTrainingCeiling?: number;
  transactionValue?: number;
  @Type(serializeType(Date)) displayCreatedOn?: Date;
  currentStatusName?: ApprovalStatusType = ApprovalStatusType.NotAvailable;
  hasSubmissionError?: boolean;

  contactId?: number;

  fringePoolId?: number;
  fringeRate?: number;
  gandaPoolId?: number;
  gandaRate?: number;
  overheadPoolId?: number;
  overheadRate?: number;
  matHandlingRate?: number;
  materialHandlePoolId?: number;
  pdfTemplateId?: number;
  txnNumber?: string;
}

export class ProjectModification {
  id: number;
  version: number;
  modType: string;
  modImpactType: string;
  modNumber: string;
  description: string;
  effectiveOn: string;
  pdfTemplateId?: number;
  contactId?: number;
  projectId: number;
  currentStatusName?: ApprovalStatusType = ApprovalStatusType.NotAvailable;
  hasSubmissionError?: boolean;
  projectName?: string;
  contactFullName?: string;
  internalMod?: boolean = false;
}


export class ProjectResourceBalance {
  id: number;
  version: number;
  projectResourceId: number;
  projectResourceName?: string;
  projectResourceLaborCategoryPositionTitle?: string;
  projectTaskBalanceId: number;
  actualHours: number = 0;
  employeeLaborITD: number = 0;
  subcontractorLaborITD: number = 0;
  employeeLaborYTD: number = 0;
  subcontractorLaborYTD: number = 0;
}


export class ProjectTaskBalance {
  id: number;
  version: number;
  projectTaskId: number;
  currentYear: string;

  currentYearEmployeeLaborITD: number = 0;
  currentYearSubcontractorLaborITD: number = 0;
  currentYearMaterialITD: number = 0;
  currentYearOdcITD: number = 0;
  currentYearLaborFringeITD: number = 0;
  currentYearLaborOverheadITD: number = 0;
  currentYearLaborMnHITD: number = 0;
  currentYearLaborGnAITD: number = 0;
  currentYearMaterialMnHITD: number = 0;
  currentYearMaterialGnAITD: number = 0;
  currentYearOdcMnHITD: number = 0;
  currentYearOdcGnAITD: number = 0;
  priorYearEmployeeLaborITD: number = 0;
  priorYearSubcontractorLaborITD: number = 0;
  priorYearMaterialITD: number = 0;
  priorYearOdcITD: number = 0;
  priorYearLaborFringeITD: number = 0;
  priorYearLaborOverheadITD: number = 0;
  priorYearLaborMnHITD: number = 0;
  priorYearLaborGnAITD: number = 0;

  priorYearMaterialMnHITD: number = 0;
  priorYearMaterialGnAITD: number = 0;

  priorYearOdcMnHITD: number = 0;
  priorYearOdcGnAITD: number = 0;

  currentYearEmployeeLaborYTD: number = 0;
  currentYearSubcontractorLaborYTD: number = 0;
  currentYearMaterialYTD: number = 0;
  currentYearOdcYTD: number = 0;
  currentYearLaborFringeYTD: number = 0;
  currentYearLaborOverheadYTD: number = 0;
  currentYearLaborMnHYTD: number = 0;
  currentYearLaborGnAYTD: number = 0;
  currentYearMaterialMnHYTD: number = 0;
  currentYearMaterialGnAYTD: number = 0;
  currentYearOdcMnHYTD: number = 0;
  currentYearOdcGnAYTD: number = 0;
  priorYearTravelITD: number = 0;
  currentYearTravelYTD: number = 0;
  currentYearTravelITD: number = 0;
  priorYearTrainingITD: number = 0;
  currentYearTrainingYTD: number = 0;
  currentYearTrainingITD: number = 0;
  @Type(serializeType(ProjectResourceBalance)) projectResourceBalances?: ProjectResourceBalance[];

}

export enum RevenueBalanceType {
  FirmFixedClin = 'FIRM_FIXED_CLIN',
  Project = 'PROJECT',
  ProjectClin = 'PROJECT_CLIN'
}

export class ProjectRevenueBalance {
  id: number;
  version: number;
  orgId: number;
  clinId: number;
  clinName?: string;
  optionNumber?: number;
  projectId: number;
  currentYear: string;
  currentYearRevenueITD: number = 0;
  priorYearRevenueITD: number = 0;
  currentYearRevenueYTD: number = 0;
  priorYearFeeITD: number = 0;
  currentYearFeeYTD: number = 0;
  currentYearFeeITD: number = 0;
}

export class ProjectTaskAvailableValues {
  id?: number;
  availableBudgetEmployeeLabor: number;
  availableBudgetSubcontractorLabor: number;
  availableBudgetMaterial: number;
  availableBudgetOtherDirect: number;
  availableBudgetFee: number;
  availableBudgetTraining: number;
}

export enum ProjectModTypeName {
  Project = 'PROJECT',
  ProjectTask = 'PROJECTTASK',
  ProjectResource = 'PROJECTRESOURCE',
  ProjectLC = 'PROJECTLABORCATEGORY',
  ProjectMilestone = 'PROJECTMILESTONE'
}

export class ProjectModImpactPanel {
  @Type(serializeType(ContractModImpact)) projects: ContractModImpact[];
  @Type(serializeType(ContractModImpact)) laborCategories: ContractModImpact[];
  @Type(serializeType(ContractModImpact)) milestones: ContractModImpact[];
  @Type(serializeType(ContractModImpact)) tasks: ContractModImpact[];
  @Type(serializeType(ContractModImpact)) resources: ContractModImpact[];
}

export enum ProjectEmit {

  AddMilestone = 'Project Milestone has been Added Successfully',
  UpdateMilestone = 'Project Milestone has been Updated Successfully',
  DeleteMilestone = 'Project Milestone has been Deleted Successfully',
  MilestoneUp = 'Milestone Position has been Shifted',
  MilestoneDown = 'Milestone Position has been Shifted',
  AddWL = 'Work Location has been Added Successfully',
  UpdateWL = 'Work Location has been Updated Successfully',
  DeleteWL = 'Work Location has been Deleted Successfully',
  AddLC = 'Labor Category has been Added Successfully',
  UpdateLC = 'Labor Category has been Updated Successfully',
  DeleteLC = 'Labor Category has been Deleted Successfully',
  AddMod = 'Modification has been Added Successfully',
  UpdateMod = 'Modification has been Updated Successfully',
  DeleteMod = 'Modification has been Deleted Successfully',
  SubmitMod = 'Modification has been submitted successfully'
}

export class ProjectSummary {
  id: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  contractId: number;
  customerId: number;
  statusName: string;
  contractTitle: string;
  customerBusinessName: string;
  totalValue: number;
  billingType?: BillingType;
  isDirect?: boolean;
}

export enum ModType {
  Unilateral = 'UNILATERAL',
  Bilateral = 'BILATERAL'
}

export enum MilestoneDueDateType {

  DaysFromStart = 'DAYS_FROM_START',
  AbsoluteDate = 'ABSOLUTE_DATE'
}
